import { getUserLocation } from "@jog/shared"
import { JoggStore } from "@jog/types"
import { kebabCase } from "lodash-es"
import { PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"

interface LocationState {
    city: string
    getLocation: () => Promise<string>
}
const DEFAULT_CITY: string = "Netherlands"
const LocationContext = createContext<LocationState>({
    city: DEFAULT_CITY,
    getLocation: () => Promise.resolve(DEFAULT_CITY),
})

export const LocationProvider = ({ children }: PropsWithChildren) => {
    const [city, setCity] = useState("")
    const getLocation = useCallback(
        () =>
            new Promise<string>((resolve, reject) => {
                if (city) return resolve(city)
                return navigator.geolocation.getCurrentPosition(async (position) => {
                    const userLocation = await getUserLocation(position.coords)
                    const city = userLocation?.city || DEFAULT_CITY
                    setCity(city)
                    resolve(city)
                }, reject)
            }),
        [city]
    )
    const value = useMemo(() => ({ city, getLocation }), [getLocation, city])
    return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>
}
export const getStoreLocator = async (inputVal: string): Promise<JoggStore[]> => {
    const value = /^\d{4}$/.test(inputVal) ? `${inputVal},NL` : inputVal
    return fetch(`${process.env.NEXT_PUBLIC_STORE_API}?q=${value}`)
        .then((response) => response.json())
        .then((res: { data: Omit<JoggStore, "slug">[] } | { message: string }) => {
            if ("message" in res) {
                throw new Error(res.message)
            }
            return res.data.map((i) => ({ ...i, slug: `${kebabCase(i.town.toLowerCase())}-${i.storeNumber}` }))
        })
        .catch((error) => {
            console.log(error)
            return []
        })
}

export const useLocation = (init = true) => {
    const [pending, setPending] = useState(init)
    const [stores, setStores] = useState<JoggStore[] | null>(null)
    const onSearch = useCallback(async (value: string) => {
        setPending(true)
        const data = await getStoreLocator(value.trim())
        setStores(data)
        setPending(false)
    }, [])
    const { city, getLocation } = useContext(LocationContext)
    useEffect(() => {
        if (city) {
            onSearch(city)
        }
    }, [city, onSearch])
    useEffect(() => {
        if (init) {
            getLocation().catch(() => onSearch("Netherlands"))
        }
    }, [getLocation, init, onSearch])
    return {
        pending,
        stores,
        onSearch,
        getLocation,
        city,
    }
}

export const useUserLocation = () => useContext(LocationContext)
